import type { CSSProperties } from 'react'
import type { Messenger } from '@/shared/api'
import { cn } from '@/shared/lib/cn'
import { Icon } from '@/shared/ui/icon'
import { messengerIcon, messengerToken } from '../model/messenger'
import s from './MessengerAvatar.module.css'

type MessengerAvatarProps = {
  messenger: Messenger
  /**
   * Ровно два размера подтверждены по прототипу (prototype/Prototype.dc.html): 36 — шапка
   * треда (isC1/isC2/isC3, `width:36px;height:36px;border-radius:10px`, иконка внутри `17×17`),
   * 30 — секция «Клиент» карточки брони (`grid-template-columns:30px 1fr auto`,
   * `border-radius:8px`, иконка `16×16`). Радиус и размер иконки не выводятся из `size` формулой
   * — 36→17px/radius10 и 30→16px/radius8 не пропорциональны (0.472 и 0.533) — поэтому это не
   * вычисление, а таблица `GEOMETRY` по двум подтверждённым точкам, а `size` закрытый юнион,
   * а не любое число: третье число сюда никто не подтверждал.
   */
  size: 36 | 30
  className?: string
}

/** Радиус плитки и размер иконки внутри — по прототипу, см. комментарий у `size` выше. */
const GEOMETRY: Record<36 | 30, { radius: number; icon: number }> = {
  36: { radius: 10, icon: 17 },
  30: { radius: 8, icon: 16 },
}

/**
 * Плитка мессенджера. Фон — `messengerToken(messenger)`, иконка внутри — БЕЛАЯ (`#fff`),
 * не `var(--on-accent)`.
 *
 * Это осознанно и НЕ совпадает с тем, как красятся инициалы в `UserAvatar`/`CompanyAvatar`/
 * `PlatformSwitcher.tileAvatar` — там `--on-accent` понадобился ради контраста в тёмной теме
 * (плитки-токены статусов в тёмной теме светлеют, и литеральный белый проваливается ниже AA).
 * Здесь другая природа элемента: это БРЕНД-иконка (лого WhatsApp/Telegram), и её узнаваемость
 * держится именно на белом на цветном в обеих темах — так буквально в макете
 * (`color:#fff` литералом что в светлой, что в тёмной, prototype/Prototype.dc.html:1357+).
 * Не переноси сюда решение про `--on-accent` из аватаров-инициалов — другая задача, другой
 * компонент.
 *
 * Email — единственное исключение, и оно ИЗ МАКЕТА, а не наше изобретение: в разметке секции
 * «Клиент» на плитке Email `color` продублирован (`color:var(--ink-2);color:#fff;` в одном
 * инлайн-стиле) на фоне `--sunk` — почти белом, — и по каскаду побеждает второе объявление
 * (`#fff`), из-за чего конверт тонет в почти белом фоне. Берём первое, осмысленное объявление
 * (`--ink-2`) — задокументированный дефект макета, `docs/plans/phase-2-bookings.md`,
 * «Дефекты макета, которые не переносим». Не «чини» это обратно на белый.
 */
export function MessengerAvatar({ messenger, size, className }: MessengerAvatarProps) {
  const { radius, icon } = GEOMETRY[size]
  const isEmail = messenger === 'email'

  return (
    <span
      className={cn(s.root, isEmail && s.email, className)}
      style={
        {
          // Единственное вычисляемое в этом слайсе — геометрия плитки по проп `size`.
          width: size,
          height: size,
          borderRadius: radius,
          '--tint': messengerToken(messenger),
        } as CSSProperties
      }
    >
      <Icon
        name={messengerIcon(messenger)}
        // У email иконка на 1px меньше и другая обводка — так в макете (15×15 sw1.7 против
        // 16×16 у WhatsApp/Telegram на той же плитке 30px); для 36px прямого образца email в
        // макете нет, экстраполируем ту же дельту (17 → 16).
        size={isEmail ? icon - 1 : icon}
        strokeWidth={isEmail ? 1.7 : undefined}
      />
    </span>
  )
}
